// Utility functions for ensuring proper color contrast

/**
 * Converts hex color to RGB
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

/**
 * Calculates the relative luminance of a color
 */
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculates the contrast ratio between two colors
 */
const getContrastRatio = (color1, color2) => {
  const rgb1 = typeof color1 === "string" ? hexToRgb(color1) : color1;
  const rgb2 = typeof color2 === "string" ? hexToRgb(color2) : color2;

  if (!rgb1 || !rgb2) return 1;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Determines if white or black text should be used for better contrast
 */
export const getContrastTextColor = (backgroundColor) => {
  if (!backgroundColor) return "#000000";

  const rgb =
    typeof backgroundColor === "string"
      ? hexToRgb(backgroundColor)
      : backgroundColor;
  if (!rgb) return "#000000";

  const whiteContrast = getContrastRatio(rgb, { r: 255, g: 255, b: 255 });
  const blackContrast = getContrastRatio(rgb, { r: 0, g: 0, b: 0 });

  // WCAG AA requires at least 4.5:1 contrast ratio
  const minContrast = 4.5;

  if (whiteContrast >= minContrast && whiteContrast > blackContrast) {
    return "#ffffff";
  } else if (blackContrast >= minContrast) {
    return "#000000";
  } else {
    // If neither meets the minimum, choose the better one
    return whiteContrast > blackContrast ? "#ffffff" : "#000000";
  }
};

/**
 * Gets a safe background color with proper contrast
 */
export const getSafeCategoryColor = (colorCode) => {
  if (!colorCode) return "#6049E7";

  // Check if the color meets contrast requirements
  const textColor = getContrastTextColor(colorCode);
  const contrast = getContrastRatio(colorCode, textColor);

  // If contrast is too low, adjust the background color
  if (contrast < 4.5) {
    // Darken the color to improve contrast
    const rgb = hexToRgb(colorCode);
    if (rgb) {
      const factor = 0.7; // Darken by 30%
      return `rgb(${Math.floor(rgb.r * factor)}, ${Math.floor(
        rgb.g * factor,
      )}, ${Math.floor(rgb.b * factor)})`;
    }
  }

  return colorCode;
};

/**
 * Gets the appropriate text color for a given background
 */
export const getCategoryTextColor = (backgroundColor) => {
  return getContrastTextColor(backgroundColor);
};
